import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Globe, LifeBuoy, Monitor, Moon, Sun } from 'lucide-react';
import { LANGUAGES } from '../i18n/index.ts';
import { useTheme, type ThemeChoice } from '../lib/theme.ts';
import { BrandMark } from './Brand.tsx';
import { APP_NAME, type FeedbackCreate } from '@vuokramani/shared';
import { errorKey } from '../lib/api.ts';
import { DEMO, SURVEY_URL } from '../lib/demo.ts';
import { useSendFeedback } from '../lib/messages.ts';
import {
  Alert,
  Button,
  buttonClass,
  Dialog,
  DialogActions,
  Notice,
  SelectField,
  TextArea,
} from './ui.tsx';
import { UserMenu } from './UserMenu.tsx';
import s from './shell.module.css';

export function LanguageSelect() {
  const { t, i18n } = useTranslation();
  return (
    <label className={s.langSelect} title={t('topbar.language')}>
      <Globe size={17} aria-hidden="true" />
      <span className="visually-hidden">{t('topbar.language')}</span>
      <select value={i18n.language} onChange={(e) => void i18n.changeLanguage(e.target.value)}>
        {LANGUAGES.map((lng) => (
          <option key={lng} value={lng}>
            {lng.toUpperCase()}
          </option>
        ))}
      </select>
    </label>
  );
}

const THEME_ORDER: ThemeChoice[] = ['system', 'light', 'dark'];
const THEME_ICON = { system: Monitor, light: Sun, dark: Moon } as const;
const THEME_LABEL = {
  system: 'topbar.themeSystem',
  light: 'topbar.themeLight',
  dark: 'topbar.themeDark',
} as const;

/** Vaihtaa teeman järjestyksessä: laitteen mukaan → vaalea → tumma. */
export function ThemeSwitch() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const Icon = THEME_ICON[theme];
  const next = THEME_ORDER[(THEME_ORDER.indexOf(theme) + 1) % THEME_ORDER.length]!;
  const label = `${t('topbar.theme')}: ${t(THEME_LABEL[theme])}`;
  return (
    <Button variant="ghost" icon onClick={() => setTheme(next)} aria-label={label} title={label}>
      <Icon size={18} />
    </Button>
  );
}

/** Apua ja palaute: viesti menee ylläpidon Tuki-osioon. Mukaan tallentuu sivu, jolla oltiin. */
function HelpDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const send = useSendFeedback();
  const [type, setType] = useState<FeedbackCreate['type']>('feedback');
  const [message, setMessage] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!message.trim()) return;
    send.mutate({ type, message: message.trim(), page: pathname.slice(0, 300) });
  }

  return (
    <Dialog open onClose={onClose} title={t('help.title')}>
      {send.isSuccess ? (
        <>
          <Notice>{t('help.sent')}</Notice>
          <DialogActions>
            <Button variant="primary" onClick={onClose}>
              {t('common.close')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={submit} style={{ display: 'grid', gap: '1.1rem' }}>
          <p style={{ color: 'var(--muted)' }}>{t('help.lead')}</p>
          <SelectField
            label={t('help.type')}
            value={type}
            onChange={(e) => setType(e.target.value as FeedbackCreate['type'])}
          >
            <option value="bug">{t('help.typeBug')}</option>
            <option value="feedback">{t('help.typeFeedback')}</option>
            <option value="question">{t('help.typeQuestion')}</option>
          </SelectField>
          <TextArea
            label={t('help.message')}
            rows={5}
            maxLength={4000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {send.isError && <Alert>{t(errorKey(send.error))}</Alert>}
          <DialogActions>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={!message.trim() || send.isPending}>
              {t('help.send')}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

interface TopbarProps {
  home: string;
  /** Näkymäkohtaiset painikkeet, esim. vuokralaisen vikailmoitus */
  actions?: ReactNode;
}

export function Topbar({ home, actions }: TopbarProps) {
  const { t } = useTranslation();
  const [helpOpen, setHelpOpen] = useState(false);
  return (
    <header className={s.topbar}>
      <div className={s.topbarLeft}>
        <Link to={home} className={`${s.brand} ${s.mobileOnly}`} aria-label={APP_NAME}>
          <BrandMark size={26} />
        </Link>
      </div>
      <div className={s.topbarRight}>
        {actions}
        {actions && <span className={`${s.divider} ${s.desktopOnly}`} />}
        <LanguageSelect />
        <ThemeSwitch />
        {DEMO ? (
          // Esittelyversiossa palaute kulkee kyselyn kautta.
          SURVEY_URL && (
            <a
              href={SURVEY_URL}
              target="_blank"
              rel="noreferrer"
              className={buttonClass('ghost')}
              title={t('demo.survey')}
            >
              <LifeBuoy size={17} aria-hidden="true" />
              <span className={s.desktopOnly}>{t('topbar.help')}</span>
            </a>
          )
        ) : (
          <Button variant="ghost" onClick={() => setHelpOpen(true)} title={t('topbar.helpLong')}>
            <LifeBuoy size={17} aria-hidden="true" />
            <span className={s.desktopOnly}>{t('topbar.help')}</span>
            <span className="visually-hidden">{t('topbar.helpLong')}</span>
          </Button>
        )}
        <span className={s.mobileOnly}>
          <UserMenu placement="down" />
        </span>
      </div>
      {helpOpen && <HelpDialog onClose={() => setHelpOpen(false)} />}
    </header>
  );
}
